import { IconButton, PopoverBase, Stack, Tooltip } from '@chronosphereio/chrono-ui';
import { Circle as CircleIcon, DeleteOutline as DeleteIcon, ChartLineVariant as ThresholdIcon } from 'mdi-material-ui';
import React from 'react';
import { ColorPicker } from '@/components/ColorPicker';
import { useChartsTheme } from '@/components/context/ChartsProvider';
import { QueryThreshold } from '@/core';
import { ColorIconButton } from '@/plugin-editor-components/components/ThresholdsEditor/ColorIconButton';

export interface QueryThresholdControlProps {
  /**
   * The current threshold on the query spec, or undefined when the query
   * is not flagged as a threshold.
   */
  value?: QueryThreshold;
  /**
   * Display label used in aria/tooltip text (e.g. the query reference letter).
   */
  queryName: string;
  /**
   * Called with the next threshold or `undefined` to clear/disable.
   */
  onChange: (next: QueryThreshold | undefined) => void;
}

/**
 * Per-query "render as threshold" toggle rendered next to the alias control.
 */
export const QueryThresholdControl = ({ value, queryName, onChange }: QueryThresholdControlProps) => {
  // Controlled so the trigger button can reflect the open state via `isSelected`.
  const [isOpen, setIsOpen] = React.useState(false);

  const {
    thresholds: { defaultColor, palette },
  } = useChartsTheme();

  const isEnabled = value !== undefined;
  const currentColor = value?.color ?? defaultColor;

  const handleEnable = () => {
    onChange({ color: defaultColor });
  };

  const handleColorChange = (color: string) => {
    onChange({ color });
  };

  const handleClear = () => {
    setIsOpen(false);
    onChange(undefined);
  };

  if (!isEnabled) {
    return (
      <Tooltip Content={`Render query ${queryName} as threshold`}>
        <IconButton size="small" aria-label={`render query ${queryName} as threshold`} onClick={handleEnable}>
          <ThresholdIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <PopoverBase
      data-testid="query threshold color picker"
      placement="right-start"
      open={isOpen}
      onClose={() => setIsOpen(false)}
      Content={
        <Stack spacing={1} sx={{ padding: 2 }}>
          <ColorPicker color={currentColor} onChange={handleColorChange} palette={[defaultColor, ...palette]} />
          <Tooltip Content={`Remove threshold from query ${queryName}`}>
            <IconButton size="small" aria-label={`remove threshold from query ${queryName}`} onClick={handleClear}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <Tooltip Content={`Edit threshold color for query ${queryName}`}>
        <ColorIconButton
          size="small"
          aria-label={`change threshold color for query ${queryName}`}
          isSelected={isOpen}
          iconColor={currentColor}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <CircleIcon />
        </ColorIconButton>
      </Tooltip>
    </PopoverBase>
  );
};
